import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  Grid,
  Group,
  ScrollArea,
  Text,
  TextInput,
  Tooltip
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { randomId } from "@mantine/hooks";
import {
  IconCheck,
  IconGripVertical,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { editFAQ, getDataEditFAQ } from "../../api/ApiConfigWeb";
import { FAQSubmit } from "../../model/FAQModel";
import QuestionEditor from "./QuestionEditor";

// Memoized editor to avoid remounts and excessive re-renders

const EditFAQ = ({
  id,
  url,
  onSuccess,
  onClose,
}: {
  id: number;
  url: string;
  onSuccess: any;
  onClose: any;
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [urlChange, setUrlChange] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const viewport = useRef<HTMLDivElement>(null);

  const form = useForm<FAQSubmit>({
    mode: "uncontrolled",
    initialValues: {
      dataSubmit: [
        {
          id: 0,
          clientId: randomId(),
          question: null,
          answer: null,
          active: true,
          createdDate: null,
          lastUpdatedDate: null,
          createdBy: null,
          lastUpdatedBy: null,
          attribute1: null,
          attribute2: null,
          attribute3: null,
          attribute4: null,
          attribute5: null,
          attribute6: null,
          attribute7: null,
          attribute8: null,
          attribute9: null,
          attribute10: null,
        },
      ],
    },
  });

  const editorsRef = React.useRef<Record<string, any>>({});

  const registerEditor = (id: string | number, editor: any) => {
    editorsRef.current[String(id)] = editor;
  };

  const fields = form.getValues().dataSubmit.map((item, index) => (
    <Draggable
      key={item.clientId ?? String(item.id)}
      index={index}
      draggableId={item.clientId ?? String(item.id)}
    >
      {(provided) => (
        <Group
          flex={"col"}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Divider
            label={
              <Badge size="lg" variant="filled" color="blue">
                CÂU HỎI SỐ {index + 1}
              </Badge>
            }
            labelPosition="center"
            w={"100%"}
            mb={-10}
            mt={10}
          />
          <Flex w={"100%"} gap={"xs"}>
            <Center {...provided.dragHandleProps}>
              <IconGripVertical size={18} />
            </Center>
            <Flex direction={"column"} w={"92.5%"}>
              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    key={form.key(`dataSubmit.${index}.question`)}
                    label="Nội dung câu hỏi"
                    placeholder="Ví dụ: Sản phẩm này có bảo hành bao lâu? Tôi có thể đổi trả trong bao lâu?"
                    withAsterisk
                    size="md"
                    {...form.getInputProps(`dataSubmit.${index}.question`)}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Box>
                    <Text fw={600} size="sm" mb={5}>
                      Nội dung câu trả lời{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Text>
                    <QuestionEditor
                      form={form}
                      index={index}
                      itemId={item.clientId ?? item.id}
                      registerEditor={registerEditor}
                      externalContent={item.answer ?? ""}
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Flex justify={"space-between"} align={"center"} w={"100%"}>
                    <Checkbox
                      mt={5}
                      label="Hiển thị câu hỏi này"
                      description="Bỏ tick để tạm ẩn câu hỏi khỏi website"
                      size="sm"
                      key={form.key(`dataSubmit.${index}.active`)}
                      {...form.getInputProps(`dataSubmit.${index}.active`, {
                        type: "checkbox",
                      })}
                    />
                    {form.getValues().dataSubmit.length > 1 ? (
                      <Tooltip
                        label="Xóa câu hỏi này khỏi danh sách"
                        position="left"
                      >
                        <ActionIcon
                          variant="filled"
                          color="red"
                          size="lg"
                          onClick={() =>
                            form.removeListItem("dataSubmit", index)
                          }
                        >
                          <IconTrash size={18} stroke={1.5} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                  </Flex>
                </Grid.Col>
              </Grid>
            </Flex>
          </Flex>
        </Group>
      )}
    </Draggable>
  ));

  const getEditFAQ = async () => {
    const res = await getDataEditFAQ(`Url=${url}`);

    console.log("[FAQ] API getDataEditFAQ result:", res);

    if (res && res.success) {
      const result = res.data;
      setUrlChange(result[0].url);
      setTitle(result[0].title);
      console.log("[FAQ] tblFaqQaCommands:", result[0].tblFaqQaCommands);
      form.setValues({
        dataSubmit: result[0].tblFaqQaCommands.map((item: any) => ({
          ...item,
          id: item.id ?? 0,
          clientId: item.id != null ? String(item.id) : randomId(),
          active: item.active === "Y" ? true : false,
        })),
      });
    }
  };

  const handleEditFAQ = async (dataSubmit: any) => {
    // flush any editor instances into the form before validation/submit
    try {
      const keys = Object.keys(editorsRef.current || {});
      for (const key of keys) {
        const editor = editorsRef.current[key];
        if (editor && typeof editor.getContent === "function") {
          const content = editor.getContent() || "";
          const idx = form
            .getValues()
              .dataSubmit.findIndex(
                (it: any) => String(it.clientId ?? it.id) === String(key)
              );
          if (idx !== -1) {
            form.setFieldValue(`dataSubmit.${idx}.answer`, content);
          }
        }
      }
    } catch (err) {
      // ignore flush errors
    }
    // re-read latest values from form after flushing editors
    dataSubmit = form.getValues().dataSubmit;
    if (
      dataSubmit.some(
        (item: any) =>
          !item.question ||
          String(item.question).length === 0 ||
          !item.answer ||
          String(item.answer).length === 0
      )
    ) {
      NotificationExtension.Fails(
        "Vui lòng nhập đầy đủ nội dung câu hỏi và câu trả lời cho tất cả các mục!"
      );
      return;
    } else if (urlChange.length === 0) {
      NotificationExtension.Fails("Vui lòng nhập đường dẫn URL!");
    } else if (!title || String(title).length === 0) {
      NotificationExtension.Fails("Vui lòng nhập tiêu đề nhóm câu hỏi!");
    } else {
      const dataModel = dataSubmit.map((item: any) => {
        const { key, title: _title, clientId, ...rest } = item;
        return {
          ...rest,
          id: item.id ?? 0,
          active: item.active ? "Y" : "N",
        };
      });
      await editFAQ(
        {
          idCategory: id,
          // url: listUrl.join(","),
          url: !urlChange.startsWith("/") ? "/" + urlChange : urlChange,
          title: title.trim(),
          tblFaqQaCommands: dataModel,
        },
        onSuccess,
        onClose
      );
    }
  };

  useEffect(() => {
    getEditFAQ();
  }, [url]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (450 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  return (
    <Box
      component="form"
      onSubmit={form.onSubmit((e: FAQSubmit) => handleEditFAQ(e.dataSubmit))}
    >
      <Grid w={"96.5%"}>
        <Grid.Col span={12}>
          <TextInput
            label="Đường dẫn URL"
            description="Nhập đường dẫn trang web mà FAQ này sẽ được hiển thị. Bắt đầu bằng dấu /"
            placeholder="Ví dụ: /laptop, /laptop-dell-inspiron-14, /may-tinh-xach-tay"
            withAsterisk
            value={urlChange}
            onChange={(e) => setUrlChange(e.currentTarget.value)}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Tiêu đề nhóm câu hỏi"
            description="Tiêu đề mô tả chủ đề chung của các câu hỏi thường gặp này"
            placeholder="Ví dụ: Câu hỏi về sản phẩm, Chính sách bảo hành, Hướng dẫn mua hàng"
            withAsterisk
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
        </Grid.Col>
      </Grid>
      <ScrollArea h={height + 130} viewportRef={viewport} mt={5}>
        <DragDropContext
          onDragEnd={({ destination, source }) =>
            destination?.index !== undefined &&
            form.reorderListItem("dataSubmit", {
              from: source.index,
              to: destination.index,
            })
          }
        >
          <Droppable droppableId="dnd-list" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {fields}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ScrollArea>

      <Group justify="center" mt="xl" gap="lg">
        <Button
          leftSection={<IconPlus size={18} />}
          color="blue"
          size="lg"
          variant="light"
          onClick={() => {
            form.insertListItem(
              "dataSubmit",
              {
                id: 0,
                clientId: randomId(),
                question: null,
                answer: null,
                active: true,
                createdDate: null,
                lastUpdatedDate: null,
                createdBy: null,
                lastUpdatedBy: null,
                attribute1: null,
                attribute2: null,
                attribute3: null,
                attribute4: null,
                attribute5: null,
                attribute6: null,
                attribute7: null,
                attribute8: null,
                attribute9: null,
                attribute10: null,
              },
              form.getValues().dataSubmit.length
            );
          }}
        >
          Thêm câu hỏi mới
        </Button>
        <Button
          leftSection={<IconCheck size={18} />}
          color="teal"
          size="lg"
          type="submit"
        >
          Lưu tất cả thay đổi
        </Button>
      </Group>
    </Box>
  );
};

export default EditFAQ;
